import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  ButtonSpinner,
  ButtonText,
  Spinner,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import ContentGroup from '@/common/components/ContentGroup';
import { useDialog } from '@/common/context/DialogContext';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';

import { ProfileScreenBody } from './components/ProfileScreenBody';
import { WebcamCaptureModal } from './components/WebcamCaptureModal';
import { useProfileForm } from './hooks/useProfileForm';

/**
 * PeopleProfileScreen
 * ----
 * Student account profile for shareable contacts and GSS discoverability.
 */
export default function PeopleProfileScreen() {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const { isFeatureActive } = useUserFeatures();
  const [webcamOpen, setWebcamOpen] = useState(false);
  const profile = useProfileForm();
  const connectionsEnabled = isFeatureActive('student-connections');

  const readFileAsBase64 = useCallback((file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  }), []);

  const handlePickFile = useCallback(async (file: File) => {
    try {
      const base64 = await readFileAsBase64(file);
      await profile.handlePhotoCaptured(base64);
    } catch {
      showToastMessage('error', t('people.photo.capture.uploadError'));
    }
  }, [profile, readFileAsBase64, showToastMessage, t]);

  if (!connectionsEnabled) {
    return null;
  }

  if (profile.isLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" testID="profile-loading">
        <Spinner size="large" />
      </Box>
    );
  }

  if (profile.isError) {
    return (
      <Box flex={1} bg="$bgApp" px="$4" py="$6" testID="profile-load-error">
        <ContentGroup variant="subtle" bg="$statusErrorSubtle">
          <VStack space="md">
            <Text size="md" color="$textOnErrorSubtle" fontWeight="medium">
              {t('people.profile.loadErrorTitle')}
            </Text>
            <Text size="sm" color="$textOnErrorSubtle">
              {t('people.profile.loadErrorBody')}
            </Text>
            <Button
              variant="outline"
              testID="profile-load-try-again"
              onPress={() => profile.refetch()}
              isDisabled={profile.isFetching}
            >
              {profile.isFetching ? <ButtonSpinner /> : null}
              <ButtonText>{t('people.profile.tryAgain')}</ButtonText>
            </Button>
          </VStack>
        </ContentGroup>
      </Box>
    );
  }

  return (
    <Box flex={1} bg="$bgApp" testID="people-profile-screen">
      <ProfileScreenBody
        form={profile.form}
        updateField={profile.updateField}
        emailError={profile.emailError}
        photoUrl={profile.photoUrl}
        hasStoredPhoto={profile.hasStoredPhoto}
        initials={profile.initials}
        firstName={profile.firstName}
        previewReach={profile.previewReach}
        hasShareableContact={profile.hasShareableContact}
        canOpenToConnect={profile.canOpenToConnect}
        isDirty={profile.isDirty}
        isSaving={profile.isSaving}
        onSave={profile.handleSave}
        onOpenWebcam={() => setWebcamOpen(true)}
        onRemovePhoto={profile.handleRemovePhoto}
        onPickFile={handlePickFile}
      />
      <WebcamCaptureModal
        isOpen={webcamOpen}
        onClose={() => setWebcamOpen(false)}
        onConfirm={profile.handlePhotoCaptured}
        onUploadInstead={() => {
          setWebcamOpen(false);
          const input = document.querySelector<HTMLInputElement>('input[type="file"][accept*="image"]');
          input?.click();
        }}
      />
    </Box>
  );
}
